import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import store from 'src/app/store/store';
import { CredentialTypeColumnFilter } from 'src/common/datagridColumnFilters/CredentialTypeColumnFilter';
import { MembershipColumnFilter } from 'src/common/datagridColumnFilters/MembershipColumnFilter';
import { VehicleColumnFilter } from 'src/common/datagridColumnFilters/VehicleColumnFilter';
import { CredentialRead } from 'src/common/external/bambi-api/bambiApi';
import { formatFullName } from 'src/common/util/formatFullName';

import {
  setSelectedCredentialTypes,
  setSelectedMembers,
  setSelectedVehicles,
} from '../credential.slice';
import { EditCredentialButton } from './EditCredentialButton';

const columnHelper = createColumnHelper<CredentialRead>();

export const columns = [
  columnHelper.accessor('membership', {
    enableSorting: true,
    id: 'membership',
    header: 'Member',
    cell: ({ row }) => {
      const membership = row.original.membership;
      return membership ? formatFullName(membership) : '';
    },
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <MembershipColumnFilter
            selectedMembers={store.getState().credential.selectedMembers}
            onChange={(values) => store.dispatch(setSelectedMembers(values))}
            open={open}
            onClose={onClose}
          />
        );
      },
      filterCount() {
        return store.getState().credential.selectedMembers.length;
      },
    },
  }),
  columnHelper.accessor('vehicle', {
    enableSorting: true,
    id: 'vehicle',
    header: 'Vehicle',
    cell: ({ row }) => {
      return row.original.vehicle?.nickname || '';
    },
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <VehicleColumnFilter
            selectedVehicles={store.getState().credential.selectedVehicles}
            onChange={(values) => store.dispatch(setSelectedVehicles(values))}
            open={open}
            onClose={onClose}
          />
        );
      },
      filterCount() {
        return store.getState().credential.selectedVehicles.length;
      },
    },
  }),
  columnHelper.accessor('credential_type.name', {
    enableSorting: true,
    id: 'credential_type',
    header: 'Name',
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <CredentialTypeColumnFilter
            selectedCredentialTypes={
              store.getState().credential.selectedCredentialTypes
            }
            onChange={(values) =>
              store.dispatch(setSelectedCredentialTypes(values))
            }
            open={open}
            onClose={onClose}
          />
        );
      },
      filterCount() {
        return store.getState().credential.selectedCredentialTypes.length;
      },
    },
  }),
  columnHelper.accessor('credential_type.description', {
    id: 'description',
    header: 'Description',
  }),
  columnHelper.accessor('expiration_date', {
    enableSorting: true,
    id: 'expiration_date',
    header: 'Expiration Date',
  }),

  columnHelper.accessor('expiration_date', {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const isExpired =
        DateTime.fromISO(row.original.expiration_date) <
        DateTime.now().startOf('day');

      const isExpiringSoon =
        DateTime.fromISO(row.original.expiration_date) <=
        DateTime.now().plus({ days: 14 }).startOf('day');

      if (isExpired) {
        return <Badge label="Expired" color="red" />;
      } else if (isExpiringSoon) {
        return (
          <div className="relative inline-block">
            <Badge label="Expiring Soon" color="yellow" />
            <div className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-pulse rounded-full bg-red-500" />
          </div>
        );
      }
      return <Badge label="Active" color="green" />;
    },
  }),

  columnHelper.display({
    id: 'actions',
    header: 'Edit',
    cell: (props) => <EditCredentialButton credential={props.row.original} />,
  }),
];
